import { useEffect, useState, useRef, useCallback } from 'react'
import {
  MS_COMMAND_AUTHENTICATE,
  MS_COMMAND_CLOSE,
  MS_COMMAND_PICK,
  MS_ERROR_UNSUPPORTED_COMMAND,
  MS_EVENT_LISTENER_MESSAGE,
  MS_MESSAGE_INIT,
  MS_MESSAGE_TYPE_ACK,
  MS_MESSAGE_TYPE_ACTIVATE,
  MS_MESSAGE_TYPE_COMMAND,
  MS_MESSAGE_TYPE_NOTIFICATION,
  MS_MESSAGE_TYPE_RESULT,
  MS_NOTIFICATION_PAGE_LOADED,
  MS_RESULT_ERROR,
  MS_RESULT_SUCCESS,
  MS_RESULT_TOKEN,
  TOPBAR_HEIGHT,
  MS_ERROR_CODES_WHITELIST,
  COLLABORATION_BANNER_HEIGHT,
  OFFICE_APP_NAME,
} from '../../constants'
import {
  extractFileTypeFromFileName,
  createFormInWindow,
  getFilePickerQueryString,
  getToken,
} from '../../helpers'
import {
  useAppDispatch,
  useSelectRunningContext,
  useSetDialog,
} from '../../hooks'
import { useSelectAuth } from '../../hooks'
import { setPage, setIsGeneralSpinner } from '../../app/root'
import { setFile } from '../../app/file'
import { setDialog } from '../../app/dialog'
import { useTrackMetricMutation } from '../../app/appApiQueries'

const OneDrivePicker = () => {
  const dispatch = useAppDispatch()
  const auth = useSelectAuth('Microsoft')
  const [isPickerActive, setIsPickerActive] = useState(false)
  const channelId = crypto.randomUUID()
  const pickerView = useRef(null)
  const isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches
  let port
  let iframeWindow = null
  const [webViewWidth, setWebViewWidth] = useState(window.innerWidth)
  const runningContext = useSelectRunningContext()
  const runningContextRef = useRef(runningContext)
  const resourceUrlRef = useRef()
  const authRef = useRef(auth)
  const metricSentRef = useRef(false) // Track if metric has been sent for this session
  const initialWebViewHeight =
    window.innerHeight -
    TOPBAR_HEIGHT -
    (runningContext === 'inCollaborate' ? COLLABORATION_BANNER_HEIGHT : 0)
  const [webViewHeight, setWebViewHeight] = useState(initialWebViewHeight)
  const [trackMetric] = useTrackMetricMutation()

  // Helper function to ensure metric is only sent once per picker session
  const trackMetricOnce = useCallback(
    (metricData) => {
      if (!metricSentRef.current) {
        metricSentRef.current = true
        trackMetric(metricData).catch(() => {
          // Swallow metric tracking errors
        })
      }
    },
    [trackMetric]
  )

  // ============================= Start V3 =============================
  // Delete me to go back to native collaboration
  const dialogTrigger = useSetDialog()
  // ============================= End V3 =============================

  const channelMessageListener = async (event) => {
    const message = event.data
    const command = message.data
    const file = command?.items?.[0]
    switch (message.type) {
      case MS_MESSAGE_TYPE_NOTIFICATION:
        if (command.error) {
          if (MS_ERROR_CODES_WHITELIST.includes(command.error.code)) {
            break
          }
          trackMetricOnce({
            measure: 'file_picker_open_success',
            tag: {
              appName: OFFICE_APP_NAME,
            },
            fields: { success: false },
          })
          dispatch(setPage('error'))
          break
        }

        if (command.notification === MS_NOTIFICATION_PAGE_LOADED) {
          // here we know that the picker page is loaded and ready for user interaction
          dispatch(setIsGeneralSpinner(false))
          trackMetricOnce({
            measure: 'file_picker_open_success',
            tag: { appName: OFFICE_APP_NAME },
            fields: { success: true },
          })
        }
        break
      case MS_MESSAGE_TYPE_COMMAND:
        port.postMessage({
          type: MS_MESSAGE_TYPE_ACK,
          id: message.id,
        })

        switch (command.command) {
          case MS_COMMAND_AUTHENTICATE: {
            const token = getToken(command, authRef.current)
            resourceUrlRef.current = command?.resource
            if (typeof token !== 'undefined' && token !== null) {
              port.postMessage({
                type: MS_MESSAGE_TYPE_RESULT,
                id: message.id,
                data: {
                  result: MS_RESULT_TOKEN,
                  token,
                },
              })
            }
            break
          }
          case MS_COMMAND_CLOSE:
            zoomSdk.sendAppToBackground().catch(() => {
              //swallow
            })
            break
          case MS_COMMAND_PICK: {
            try {
              const name = file?.name
              const type = extractFileTypeFromFileName(name)

              dispatch(
                setFile({
                  name,
                  url: file.webUrl,
                  type,
                  id: file.sharepointIds?.listItemUniqueId, // using the sourceDoc id here for the embedded file picker
                  driveId: file.parentReference?.driveId,
                  siteUrl: file.sharepointIds?.siteUrl,
                  resourceUrl: resourceUrlRef.current,
                })
              )

              // ============================= Start V3 =============================
              // Delete me to go back to native collaboration
              if (
                runningContextRef?.current === 'inMainClient' ||
                runningContextRef?.current === 'inChat'
              ) {
                zoomSdk.openUrl({ url: file.webUrl })
              } else if (runningContextRef?.current === 'inCollaborate') {
                dispatch(setDialog({ type: 'collaborationNoShare' }))
              } else {
                dialogTrigger({
                  drive: 'Microsoft',
                  fileId: file.id,
                  fileDriveId: file.parentReference?.driveId,
                })
              }
              // ============================= End V3 =============================

              port.postMessage({
                type: MS_MESSAGE_TYPE_RESULT,
                id: message.id,
                data: {
                  result: MS_RESULT_SUCCESS,
                },
              })
            } catch (err) {
              dispatch(setPage('error'))
            }

            break
          }
          default:
            port.postMessage({
              result: MS_RESULT_ERROR,
              error: {
                code: MS_ERROR_UNSUPPORTED_COMMAND,
                message: command.command,
              },
              isExpected: true,
            })
            break
        }
        break
      default:
        // something weird happened, error?
        dispatch(setPage('error'))
        break
    }
  }

  function initializeMessageListener(event) {
    // we validate the message is for us, win here is the same variable as above
    if (event.source && event.source === iframeWindow) {
      const message = event.data

      // the channelId is part of the configuration options, but we could have multiple pickers so that is supported via channels
      // On initial load and if it ever refreshes in its window, the Picker will send an 'initialize' message.
      // Communication with the picker should subsequently take place using a `MessageChannel`.
      if (message.type === MS_MESSAGE_INIT && message.channelId === channelId) {
        // grab the port from the event
        port = event.ports[0]

        // add an event listener to the port (example implementation is in the next section)
        port.addEventListener(MS_EVENT_LISTENER_MESSAGE, channelMessageListener)

        // start ("open") the port
        port.start()

        // tell the picker to activate
        port.postMessage({
          type: MS_MESSAGE_TYPE_ACTIVATE,
        })
      }
    }
  }

  useEffect(() => {
    if (auth.accessToken) {
      authRef.current = auth
      // Reset metric tracking for new auth/session
      metricSentRef.current = false
    }
  }, [auth])

  useEffect(() => {
    runningContextRef.current = runningContext
  }, [runningContext])

  useEffect(() => {
    dispatch(setIsGeneralSpinner(true))
  }, [dispatch])

  // ============================= Start V3 =============================
  // Uncomment me to go back to native collaboration
  // useEffect(() => {
  //   if (runningContext === 'inMeeting' || runningContext === 'inCollaborate') {
  //     zoomSdk.expandApp({ action: 'expand' })
  //   }
  // }, [runningContext])
  // ============================= End V3 =============================

  useEffect(() => {
    async function showFilePicker() {
      try {
        if (pickerView.current !== null) {
          // eslint-disable-next-line react-hooks/exhaustive-deps
          iframeWindow = pickerView.current.contentWindow
        } else {
          dispatch(setPage('error'))
        }

        const queryString = getFilePickerQueryString(
          channelId,
          window.origin,
          isDarkMode
        )

        const form = createFormInWindow(iframeWindow, queryString, auth)
        form.submit()
        window.addEventListener(
          MS_EVENT_LISTENER_MESSAGE,
          initializeMessageListener
        )
        setIsPickerActive(true)
      } catch (err) {
        dispatch(setPage('error'))
      }
    }
    if (auth && !isPickerActive) showFilePicker()
  }, [auth, isPickerActive, dispatch, runningContext])

  const handleWindowSizeChange = useCallback(() => {
    const height =
      window.innerHeight -
      TOPBAR_HEIGHT -
      (runningContext === 'inCollaborate' ? COLLABORATION_BANNER_HEIGHT : 0)

    setWebViewHeight(height)
    setWebViewWidth(window.innerWidth)
  }, [runningContext])

  // if the running context changes at all
  useEffect(() => {
    handleWindowSizeChange()
  }, [runningContext, handleWindowSizeChange])

  useEffect(() => {
    window.addEventListener('resize', handleWindowSizeChange)
  })

  // ============================= Start V3 =============================
  // Add code back to enable Native Collaboration
  /*
  useEffect(() => {
    if (
      runningContext &&
      runningContext !== 'inMainClient' &&
      runningContext !== 'inCollaborate'
    ) {
      zoomSdk
        .zmSetCollaborateButtonState({ setState: 'disabled' })
        .catch(() => {
          // swallow
        })
    }
  }, [runningContext])
  */
  // ============================= End V3 =============================

  return (
    <div style={{ width: `${webViewWidth}px`, height: `${webViewHeight}px` }}>
      <iframe
        title='unique'
        ref={pickerView}
        className='h-full w-full'
        sandbox='allow-same-origin allow-forms allow-scripts'
      />
    </div>
  )
}

export default OneDrivePicker
